import { useCallback, useSyncExternalStore } from 'react';

export type ReportFavoriteRef =
  | { kind: 'seeded'; key: string }
  | { kind: 'custom'; id: string };

export const favoriteRefKey = (ref: ReportFavoriteRef): string =>
  ref.kind === 'seeded' ? `seeded:${ref.key}` : `custom:${ref.id}`;

export const parseFavoriteRefKey = (value: string): ReportFavoriteRef | null => {
  const [kind, id] = value.split(':') as ['seeded' | 'custom', string];
  if (!id) return null;
  if (kind === 'seeded') return { kind: 'seeded', key: id };
  if (kind === 'custom') return { kind: 'custom', id };
  return null;
};

const FAVORITES_CHANGED_EVENT = 'trinity:report-favorites-changed';

const storageKeyFor = (churchId: string, userId: string): string =>
  `trinity.reports.favorites.${churchId}.${userId}`;

const readFavorites = (storageKey: string | null): ReportFavoriteRef[] => {
  if (!storageKey) return [];
  try {
    const raw = localStorage.getItem(storageKey);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];
    return parsed
      .map((item) => {
        if (typeof item !== 'string') return null;
        return parseFavoriteRefKey(item);
      })
      .filter((item): item is ReportFavoriteRef => item !== null);
  } catch {
    return [];
  }
};

const favoritesEqual = (left: ReportFavoriteRef[], right: ReportFavoriteRef[]): boolean =>
  left.length === right.length &&
  left.every((item, index) => favoriteRefKey(item) === favoriteRefKey(right[index]!));

let snapshotCache: {
  storageKey: string | null;
  favorites: ReportFavoriteRef[];
} = { storageKey: null, favorites: [] };

const getSnapshot = (storageKey: string | null): ReportFavoriteRef[] => {
  if (!storageKey) return [];
  const favorites = readFavorites(storageKey);
  if (
    snapshotCache.storageKey === storageKey &&
    favoritesEqual(snapshotCache.favorites, favorites)
  ) {
    return snapshotCache.favorites;
  }
  snapshotCache = { storageKey, favorites };
  return favorites;
};

const invalidateSnapshotCache = (): void => {
  snapshotCache = { storageKey: null, favorites: [] };
};

const writeFavorites = (storageKey: string, favorites: ReportFavoriteRef[]): void => {
  try {
    localStorage.setItem(storageKey, JSON.stringify(favorites.map(favoriteRefKey)));
    queueMicrotask(() => {
      window.dispatchEvent(
        new CustomEvent(FAVORITES_CHANGED_EVENT, { detail: { storageKey } }),
      );
    });
  } catch {
    // Ignore quota / private-mode failures.
  }
};

const subscribe = (listener: () => void): (() => void) => {
  const onStorage = () => {
    invalidateSnapshotCache();
    listener();
  };
  const onChanged = () => {
    invalidateSnapshotCache();
    listener();
  };
  window.addEventListener('storage', onStorage);
  window.addEventListener(FAVORITES_CHANGED_EVENT, onChanged);
  return () => {
    window.removeEventListener('storage', onStorage);
    window.removeEventListener(FAVORITES_CHANGED_EVENT, onChanged);
  };
};

export const useReportFavorites = (churchId: string | undefined, userId: string | undefined) => {
  const storageKey = churchId && userId ? storageKeyFor(churchId, userId) : null;
  const favorites = useSyncExternalStore(
    subscribe,
    () => getSnapshot(storageKey),
    () => [],
  );

  const isFavorite = useCallback(
    (ref: ReportFavoriteRef) => favorites.some((item) => favoriteRefKey(item) === favoriteRefKey(ref)),
    [favorites],
  );

  const setFavorite = useCallback(
    (ref: ReportFavoriteRef, next: boolean) => {
      if (!storageKey) return;
      const current = readFavorites(storageKey);
      const key = favoriteRefKey(ref);
      const updated = next
        ? current.some((item) => favoriteRefKey(item) === key)
          ? current
          : [...current, ref]
        : current.filter((item) => favoriteRefKey(item) !== key);
      if (favoritesEqual(current, updated)) return;
      writeFavorites(storageKey, updated);
    },
    [storageKey],
  );

  const toggleFavorite = useCallback(
    (ref: ReportFavoriteRef) => {
      setFavorite(ref, !isFavorite(ref));
    },
    [isFavorite, setFavorite],
  );

  return {
    favorites,
    isFavorite,
    setFavorite,
    toggleFavorite,
  };
};
